import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  FormDialog,
  FilterDialogField,
} from '@shared/models';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatChipInputEvent, MatChipListbox, MatChipOption, MatChipGrid, MatChipRow, MatChipRemove, MatChipInput } from '@angular/material/chips';
import { NgTemplateOutlet } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { ZDialogComponent } from '../z-dialog/z-dialog.component';
import { ZInputComponent } from '../z-input/z-input.component';
import { MatFormField, MatInput, MatSuffix } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { ZButtonComponent } from '../z-button/z-button.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'filter-dialog',
    templateUrl: './filter-dialog.component.html',
    styleUrls: ['./filter-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgTemplateOutlet, MatButton, ZDialogComponent, ReactiveFormsModule, ZInputComponent, MatChipListbox, MatChipOption, MatFormField, MatChipGrid, MatChipRow, MatChipRemove, MatIcon, MatChipInput, MatSelect, MatOption, MatInput, MatDatepickerInput, MatDatepickerToggle, MatSuffix, MatDatepicker, ZButtonComponent, TranslatePipe]
})
export class FilterDialogComponent implements OnInit {
  @Input() dataInput: FormDialog | null = null;
  @Input() asPopover = false;
  @Output() applied = new EventEmitter<Record<string, unknown>>();
  @Output() cleared = new EventEmitter<void>();

  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  formGroup = new FormGroup<Record<string, FormControl>>({});
  private initialized = false;

  constructor(
    @Optional() private dialogRef: MatDialogRef<FilterDialogComponent> | null,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: FormDialog | null,
  ) { }

  get resolvedData(): FormDialog | null {
    return this.dataInput || this.dialogData;
  }

  get title(): string {
    return this.resolvedData?.title || '';
  }

  get actionButton(): string {
    return this.resolvedData?.actionButton || 'COMMON.SEARCH';
  }

  get fields(): FilterDialogField[] {
    return this.resolvedData?.fields || [];
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataInput'] && !changes['dataInput'].firstChange) {
      this.initializeForm();
    }
  }

  private initializeForm(): void {
    if (!this.resolvedData) {
      return;
    }

    this.formGroup = new FormGroup({});
    this.formGroup.reset();

    this.fields.forEach((field) => {
      const defaultValue = this.resolveModernDefaultValue(field);
      this.formGroup.addControl(field.key, new FormControl(defaultValue));
    });
    this.formGroup.updateValueAndValidity();

    this.initialized = true;
  }

  addChip(fieldKey: string, event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (!value) {
      return;
    }

    const control = this.formGroup.get(fieldKey) as FormControl<string[]>;
    const current = control.value || [];
    control.setValue([...current, value]);
    event.chipInput?.clear();
  }

  removeChip(fieldKey: string, chipValue: string): void {
    const control = this.formGroup.get(fieldKey) as FormControl<string[]>;
    const current = control.value || [];
    control.setValue(current.filter((item) => item !== chipValue));
  }

  chipsValue(fieldKey: string): string[] {
    return ((this.formGroup.get(fieldKey) as FormControl<string[]>)?.value || []) as string[];
  }

  /** FormControl do campo, para inputs compartilhados (z-input) que recebem o control. */
  control(fieldKey: string): FormControl {
    return this.formGroup.get(fieldKey) as FormControl;
  }

  isOptionsChips(field: FilterDialogField): boolean {
    return field.type === 'chips' && !!field.options?.length;
  }

  private resolveModernDefaultValue(field: FilterDialogField): unknown {
    if (field.type !== 'chips') {
      return null;
    }

    if (field.options?.length) {
      return field.multiple ? [] : null;
    }

    return [];
  }

  filter(): void {
    const payload = this.formGroup.getRawValue() as Record<string, unknown>;
    this.applied.emit(payload);

    if (this.dialogRef) {
      this.dialogRef.close(payload);
    }
  }

  clear(): void {
    if (!this.initialized) {
      return;
    }

    this.fields.forEach((field) => {
      this.formGroup.get(field.key)?.setValue(this.resolveModernDefaultValue(field));
    });

    this.cleared.emit();
  }

}
